<template>
  <div class="competition-archive-page">
    <div class="archive-header">
      <div class="archive-header-inner">
        <el-button type="text" class="back-btn" icon="el-icon-arrow-left" @click="goBack">返回</el-button>
        <div>
          <h1 class="archive-page-title">竞赛档案</h1>
          <p class="archive-page-subtitle">学生上传资料，管理员与机构可审核，家长仅查看绑定学生资料和审核意见</p>
        </div>
      </div>
    </div>

    <div class="archive-body" v-loading="loading">
      <el-row :gutter="20">
        <el-col :lg="8" :md="24">
          <el-card class="archive-card overview-card" shadow="never">
            <div slot="header" class="card-header">
              <span>档案总览</span>
              <el-tag size="mini">{{ roleText }}</el-tag>
            </div>
            <div class="overview-meta">
              <div class="meta-item">
                <span class="meta-label">当前角色</span>
                <span class="meta-value">{{ roleText }}</span>
              </div>
              <div class="meta-item">
                <span class="meta-label">档案数量</span>
                <span class="meta-value">{{ archiveList.length }}</span>
              </div>
            </div>
            <div v-if="linkedStudents.length" class="student-switcher">
              <div class="switcher-title">查看学生</div>
              <el-select v-model="selectedStudentId" placeholder="请选择学生" size="small" style="width: 100%" @change="loadOverview">
                <el-option v-for="item in linkedStudents" :key="item.studentId" :label="`${item.studentName || '未命名学生'}${item.studentNum ? '（' + item.studentNum + '）' : ''}`" :value="item.studentId" />
              </el-select>
            </div>
            <el-button v-if="canEditCurrent" type="primary" icon="el-icon-plus" style="width: 100%; margin-top: 16px;" @click="startCreate">新增竞赛档案</el-button>
          </el-card>

          <el-card class="archive-card archive-list-card" shadow="never">
            <div slot="header" class="card-header">
              <span>档案列表</span>
            </div>
            <div v-if="archiveList.length">
              <div
                v-for="item in archiveList"
                :key="item.id"
                class="archive-list-item"
                :class="{ active: currentArchive && currentArchive.id === item.id }"
                @click="selectArchive(item)"
              >
                <div class="archive-list-title">{{ item.competitionName }}</div>
                <div class="archive-list-subtitle">{{ item.competitionYear || '-' }} · {{ item.awardResult || '待补充结果' }}</div>
                <div class="archive-status-row">
                  <el-tag size="mini" :type="reviewStatusType(item.reviewStatus)">{{ reviewStatusText(item.reviewStatus) }}</el-tag>
                </div>
              </div>
            </div>
            <el-empty v-else :description="archiveListEmptyText"></el-empty>
          </el-card>
        </el-col>

        <el-col :lg="16" :md="24">
          <el-card class="archive-card form-card" shadow="never">
            <div slot="header" class="card-header action-header">
              <span>{{ form.id ? (canEditCurrent ? '编辑竞赛档案' : '查看竞赛档案') : '新建竞赛档案' }}</span>
              <div>
                <el-button v-if="form.id && canEditCurrent" type="danger" plain size="small" @click="handleDelete">删除</el-button>
                <el-button v-if="canEditCurrent" type="primary" size="small" :loading="saving" @click="handleSave">保存</el-button>
              </div>
            </div>

            <template v-if="archiveList.length || canEditCurrent">
              <div v-if="form.id" class="review-banner">
              <div class="review-banner-main">
                <span class="review-banner-label">审核状态</span>
                <el-tag :type="reviewStatusType(form.reviewStatus)">{{ reviewStatusText(form.reviewStatus) }}</el-tag>
                <span v-if="form.reviewTime" class="review-banner-time">审核时间：{{ formatDateTime(form.reviewTime) }}</span>
              </div>
              <div v-if="form.reviewComment" class="review-comment-box">
                <span class="review-banner-label">审核意见</span>
                <div class="review-comment-text">{{ form.reviewComment }}</div>
              </div>
              </div>

              <el-form ref="archiveForm" :model="form" :rules="rules" label-width="100px">
              <el-row :gutter="16">
                <el-col :span="12">
                  <el-form-item label="竞赛名称" prop="competitionName">
                    <el-input v-model="form.competitionName" :disabled="!canEditCurrent" placeholder="请输入竞赛名称"></el-input>
                  </el-form-item>
                </el-col>
                <el-col :span="12">
                  <el-form-item label="主办方">
                    <el-input v-model="form.organizer" :disabled="!canEditCurrent" placeholder="请输入主办方"></el-input>
                  </el-form-item>
                </el-col>
                <el-col :span="12">
                  <el-form-item label="竞赛分类">
                    <el-input v-model="form.competitionCategory" :disabled="!canEditCurrent" placeholder="如数学、编程、商科"></el-input>
                  </el-form-item>
                </el-col>
                <el-col :span="12">
                  <el-form-item label="竞赛级别">
                    <el-input v-model="form.competitionLevel" :disabled="!canEditCurrent" placeholder="如国际 / 全国 / 区域"></el-input>
                  </el-form-item>
                </el-col>
                <el-col :span="12">
                  <el-form-item label="参赛组别">
                    <el-input v-model="form.groupName" :disabled="!canEditCurrent" placeholder="请输入参赛组别"></el-input>
                  </el-form-item>
                </el-col>
                <el-col :span="12">
                  <el-form-item label="参赛年份">
                    <el-input v-model="form.competitionYear" :disabled="!canEditCurrent" placeholder="如 2026"></el-input>
                  </el-form-item>
                </el-col>
                <el-col :span="12">
                  <el-form-item label="参与日期">
                    <el-date-picker v-model="form.participationDate" :disabled="!canEditCurrent" type="date" value-format="yyyy-MM-dd" placeholder="选择日期" style="width: 100%" />
                  </el-form-item>
                </el-col>
                <el-col :span="12">
                  <el-form-item label="获奖等级">
                    <el-input v-model="form.awardLevel" :disabled="!canEditCurrent" placeholder="如一等奖、金奖"></el-input>
                  </el-form-item>
                </el-col>
                <el-col :span="24">
                  <el-form-item label="获奖结果">
                    <el-input v-model="form.awardResult" :disabled="!canEditCurrent" placeholder="如晋级决赛、获得区域优胜"></el-input>
                  </el-form-item>
                </el-col>
                <el-col :span="24">
                  <el-form-item label="项目说明">
                    <el-input v-model="form.description" :disabled="!canEditCurrent" type="textarea" :rows="4" placeholder="补充竞赛经历、项目说明、作品简介等"></el-input>
                  </el-form-item>
                </el-col>
              </el-row>
              </el-form>

              <div class="section-block">
              <div class="section-block-header">
                <h3>上传材料</h3>
                <el-button v-if="canEditCurrent" size="mini" @click="addAttachment">新增材料</el-button>
              </div>
              <div v-for="(item, index) in form.attachments" :key="`attachment-${index}`" class="dynamic-card">
                <el-row :gutter="12">
                  <el-col :span="6">
                    <el-select v-model="item.bizType" :disabled="!canEditCurrent" placeholder="材料类型" size="small" style="width: 100%">
                      <el-option v-for="type in attachmentTypeOptions" :key="type.value" :label="type.label" :value="type.value" />
                    </el-select>
                  </el-col>
                  <el-col :span="10">
                    <el-input v-model="item.fileName" :disabled="!canEditCurrent" size="small" placeholder="文件名称"></el-input>
                  </el-col>
                  <el-col v-if="canEditCurrent" :span="5">
                    <el-upload
                      :show-file-list="false"
                      :http-request="(uploadRequest) => handleAttachmentUpload(uploadRequest, item)"
                      action="#"
                    >
                      <el-button size="small" icon="el-icon-upload2">上传文件</el-button>
                    </el-upload>
                  </el-col>
                  <el-col :span="canEditCurrent ? 3 : 8" class="align-right">
                    <el-button v-if="canEditCurrent" type="text" class="danger-text" @click="removeAttachment(index)">删除</el-button>
                  </el-col>
                </el-row>
                <div v-if="item.fileUrl" class="file-preview-row">
                  <a :href="normalizePreviewUrl(item)" target="_blank" rel="noopener noreferrer">预览 / 打开 {{ item.fileName || '文件' }}</a>
                </div>
              </div>
              <el-empty v-if="!form.attachments.length" description="暂无上传材料"></el-empty>
              </div>

              <div class="section-block">
              <div class="section-block-header">
                <h3>参与活动记录</h3>
                <el-button v-if="canEditCurrent" size="mini" @click="addActivity">新增记录</el-button>
              </div>
              <div v-for="(item, index) in form.activities" :key="`activity-${index}`" class="dynamic-card">
                <el-row :gutter="12">
                  <el-col :span="6">
                    <el-input v-model="item.activityType" :disabled="!canEditCurrent" size="small" placeholder="活动类型"></el-input>
                  </el-col>
                  <el-col :span="10">
                    <el-input v-model="item.title" :disabled="!canEditCurrent" size="small" placeholder="活动标题"></el-input>
                  </el-col>
                  <el-col :span="5">
                    <el-date-picker v-model="item.activityDate" :disabled="!canEditCurrent" type="date" value-format="yyyy-MM-dd" size="small" placeholder="活动日期" style="width: 100%" />
                  </el-col>
                  <el-col :span="3" class="align-right" v-if="canEditCurrent">
                    <el-button type="text" class="danger-text" @click="removeActivity(index)">删除</el-button>
                  </el-col>
                </el-row>
                <el-input v-model="item.description" :disabled="!canEditCurrent" type="textarea" :rows="2" placeholder="请补充该活动的经过、成绩或收获" style="margin-top: 12px"></el-input>
              </div>
              <el-empty v-if="!form.activities.length" description="暂无活动记录"></el-empty>
              </div>

              <div v-if="canReviewCurrent && form.id" class="section-block review-panel">
              <div class="section-block-header">
                <h3>资料审核</h3>
              </div>
              <el-form label-width="100px">
                <el-form-item label="审核意见">
                  <el-input v-model="reviewForm.reviewComment" type="textarea" :rows="4" placeholder="请输入审核意见，驳回时建议说明原因"></el-input>
                </el-form-item>
              </el-form>
              <div class="review-actions">
                <el-button type="success" :loading="reviewing" @click="handleReview(1)">审核通过</el-button>
                <el-button type="danger" :loading="reviewing" @click="handleReview(2)">审核驳回</el-button>
              </div>
              </div>
            </template>

            <el-empty v-else :description="detailEmptyText"></el-empty>
          </el-card>
        </el-col>
      </el-row>
    </div>
  </div>
</template>

<script>
import {
  deleteCompetitionArchive,
  getCompetitionArchiveDetail,
  getCompetitionArchiveOverview,
  reviewCompetitionArchive,
  saveCompetitionArchive,
  uploadCompetitionArchiveFile
} from '@/api/competitionArchive'

const createDefaultForm = (studentId = null) => ({
  id: null,
  studentId,
  competitionName: '',
  organizer: '',
  competitionCategory: '',
  competitionLevel: '',
  groupName: '',
  competitionYear: '',
  participationDate: '',
  awardLevel: '',
  awardResult: '',
  description: '',
  attachments: [],
  activities: []
})

export default {
  name: 'CompetitionArchive',
  data() {
    return {
      loading: false,
      saving: false,
      overviewRequestId: 0,
      overview: null,
      archiveList: [],
      currentArchive: null,
      linkedStudents: [],
      selectedStudentId: null,
      currentRole: '',
      form: createDefaultForm(),
      reviewing: false,
      reviewForm: {
        reviewComment: ''
      },
      attachmentTypeOptions: [
        { label: '证书图片', value: 'certificate' },
        { label: '证书PDF', value: 'pdf' },
        { label: '代码文件', value: 'code' },
        { label: '作品照片', value: 'photo' },
        { label: '演示视频', value: 'video' },
        { label: '其他资料', value: 'material' }
      ],
      rules: {
        competitionName: [
          { required: true, message: '请输入竞赛名称', trigger: 'blur' }
        ]
      }
    }
  },
  computed: {
    roleText() {
      const map = {
        role_student: '学生端',
        role_parent: '家长端',
        role_admin: '管理员',
        role_organization: '机构端'
      }
      return map[this.currentRole] || '用户'
    },
    canEditCurrent() {
      return this.currentRole === 'role_student'
    },
    canReviewCurrent() {
      return this.currentRole === 'role_admin' || this.currentRole === 'role_organization'
    },
    archiveListEmptyText() {
      if (this.currentRole === 'role_organization') {
        return this.linkedStudents.length ? '暂无可查看资料' : '当前机构暂无绑定学生'
      }
      if (this.currentRole === 'role_parent') {
        return this.linkedStudents.length ? '暂无可查看资料' : '当前账号暂无绑定学生'
      }
      return '暂无竞赛档案'
    },
    detailEmptyText() {
      if (this.currentRole === 'role_organization') {
        return this.linkedStudents.length ? '暂无可查看资料' : '当前机构暂无绑定学生'
      }
      if (this.currentRole === 'role_parent') {
        return this.linkedStudents.length ? '暂无可查看资料' : '当前账号暂无绑定学生'
      }
      return '暂无竞赛档案'
    }
  },
  created() {
    this.loadOverview()
  },
  methods: {
    resetDetailState(studentId = null) {
      this.currentArchive = null
      this.form = createDefaultForm(studentId)
      this.reviewForm.reviewComment = ''
    },
    goBack() {
      if (window.history.length > 1) {
        this.$router.back()
        return
      }
      if (this.currentRole === 'role_admin') {
        this.$router.push('/dashboard')
        return
      }
      if (this.currentRole === 'role_organization') {
        this.$router.push('/org/dashboard')
        return
      }
      if (this.currentRole === 'role_parent') {
        this.$router.push('/dashboard')
        return
      }
      this.$router.push('/dashboard')
    },
    async loadOverview() {
      this.loading = true
      const requestId = ++this.overviewRequestId
      try {
        this.resetDetailState(this.selectedStudentId)
        const res = await getCompetitionArchiveOverview(this.selectedStudentId)
        if (requestId !== this.overviewRequestId) {
          return
        }
        if (res && (res.code === 1 || res.code === 200)) {
          const data = res.data || {}
          this.overview = data
          this.currentRole = data.currentRole || ''
          this.linkedStudents = data.linkedStudents || []
          this.archiveList = data.archives || []
          if (!this.selectedStudentId && data.currentStudentId) {
            this.selectedStudentId = data.currentStudentId
          }
          if (this.archiveList.length) {
            await this.selectArchive(this.archiveList[0])
          } else {
            this.startCreate()
          }
        } else {
          this.$message.error(res?.msg || '获取竞赛档案失败')
        }
      } catch (e) {
        if (requestId !== this.overviewRequestId) {
          return
        }
        console.error(e)
        this.$message.error('获取竞赛档案失败')
      } finally {
        if (requestId !== this.overviewRequestId) {
          return
        }
        this.loading = false
      }
    },
    async selectArchive(item) {
      if (!item || !item.id) return
      const res = await getCompetitionArchiveDetail(item.id)
      if (res && (res.code === 1 || res.code === 200) && res.data) {
        this.currentArchive = res.data
        this.form = {
          ...createDefaultForm(this.selectedStudentId),
          ...res.data,
          attachments: (res.data.attachments || []).map(file => ({ ...file })),
          activities: (res.data.activities || []).map(activity => ({ ...activity }))
        }
        this.reviewForm.reviewComment = res.data.reviewComment || ''
      }
    },
    startCreate() {
      if (!this.canEditCurrent) return
      this.currentArchive = null
      this.form = createDefaultForm(this.selectedStudentId)
      this.reviewForm.reviewComment = ''
    },
    addAttachment() {
      if (!this.canEditCurrent) return
      this.form.attachments.push({
        bizType: 'certificate',
        fileType: '',
        fileName: '',
        filePath: '',
        fileUrl: '',
        mimeType: '',
        fileSize: null
      })
    },
    removeAttachment(index) {
      if (!this.canEditCurrent) return
      this.form.attachments.splice(index, 1)
    },
    addActivity() {
      if (!this.canEditCurrent) return
      this.form.activities.push({
        activityType: '',
        title: '',
        description: '',
        activityDate: '',
        sortOrder: this.form.activities.length
      })
    },
    removeActivity(index) {
      if (!this.canEditCurrent) return
      this.form.activities.splice(index, 1)
    },
    async handleAttachmentUpload(uploadRequest, item) {
      try {
        const file = uploadRequest.file
        const res = await uploadCompetitionArchiveFile(file)
        if (res && (res.code === 1 || res.code === 200) && res.data) {
          item.fileName = file.name
          item.filePath = res.data.path
          item.fileUrl = res.data.url
          item.fileType = file.name.includes('.') ? file.name.split('.').pop().toLowerCase() : ''
          item.mimeType = file.type
          item.fileSize = file.size
          this.$message.success('上传成功')
        } else {
          this.$message.error(res?.msg || '上传失败')
        }
      } catch (e) {
        console.error(e)
        this.$message.error('上传失败')
      } finally {
        if (uploadRequest.onSuccess) {
          uploadRequest.onSuccess({}, uploadRequest.file)
        }
      }
    },
    normalizePreviewUrl(item) {
      if (item.fileUrl && item.fileUrl.startsWith('http')) {
        return item.fileUrl
      }
      const path = item.filePath || ''
      return `/api/file/preview?path=${encodeURIComponent(path)}`
    },
    reviewStatusText(status) {
      const map = {
        1: '审核通过',
        2: '审核驳回'
      }
      return map[Number(status)] || '待审核'
    },
    reviewStatusType(status) {
      const map = {
        1: 'success',
        2: 'danger'
      }
      return map[Number(status)] || 'warning'
    },
    formatDateTime(value) {
      if (!value) return '-'
      const s = String(value)
      return s.length >= 19 ? s.slice(0, 19).replace('T', ' ') : s.replace('T', ' ')
    },
    async handleSave() {
      if (!this.canEditCurrent) return
      try {
        await this.$refs.archiveForm.validate()
      } catch (e) {
        return
      }
      this.saving = true
      try {
        const payload = {
          ...this.form,
          studentId: this.selectedStudentId || this.form.studentId,
          attachments: (this.form.attachments || []).filter(item => item.filePath),
          activities: (this.form.activities || []).filter(item => item.title)
        }
        const res = await saveCompetitionArchive(payload)
        if (res && (res.code === 1 || res.code === 200)) {
          this.$message.success('保存成功')
          await this.loadOverview()
          if (res.data && res.data.id) {
            const target = this.archiveList.find(item => item.id === res.data.id)
            if (target) {
              await this.selectArchive(target)
            }
          }
        } else {
          this.$message.error(res?.msg || '保存失败')
        }
      } catch (e) {
        console.error(e)
        this.$message.error('保存失败')
      } finally {
        this.saving = false
      }
    },
    async handleReview(reviewStatus) {
      if (!this.canReviewCurrent || !this.form.id) return
      this.reviewing = true
      try {
        const res = await reviewCompetitionArchive({
          archiveId: this.form.id,
          reviewStatus,
          reviewComment: this.reviewForm.reviewComment
        })
        if (res && (res.code === 1 || res.code === 200)) {
          this.$message.success('审核成功')
          await this.loadOverview()
          const target = this.archiveList.find(item => item.id === this.form.id)
          if (target) {
            await this.selectArchive(target)
          }
        } else {
          this.$message.error(res?.msg || '审核失败')
        }
      } catch (e) {
        console.error(e)
        this.$message.error('审核失败')
      } finally {
        this.reviewing = false
      }
    },
    handleDelete() {
      if (!this.canEditCurrent) return
      if (!this.form.id) return
      this.$confirm('确定删除当前竞赛档案吗？', '提示', { type: 'warning' }).then(async () => {
        const res = await deleteCompetitionArchive(this.form.id)
        if (res && (res.code === 1 || res.code === 200)) {
          this.$message.success('删除成功')
          await this.loadOverview()
        } else {
          this.$message.error(res?.msg || '删除失败')
        }
      }).catch(() => {})
    }
  }
}
</script>

<style scoped>
.competition-archive-page {
  min-height: 100vh;
  background: #f5f7fa;
}

.archive-header {
  background: linear-gradient(135deg, #1f3c88 0%, #39a0ed 100%);
  padding: 24px 0;
}

.archive-header-inner,
.archive-body {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 24px;
}

.archive-header-inner {
  display: flex;
  align-items: center;
  gap: 16px;
}

.back-btn,
.archive-page-title,
.archive-page-subtitle {
  color: #fff;
}

.archive-page-title {
  font-size: 30px;
  font-weight: 700;
  margin: 0 0 8px;
}

.archive-page-subtitle {
  margin: 0;
  opacity: 0.9;
}

.archive-body {
  padding-top: 24px;
  padding-bottom: 40px;
}

.archive-card {
  border-radius: 16px;
  margin-bottom: 20px;
}

.card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-weight: 600;
}

.action-header {
  align-items: center;
}

.overview-meta {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.meta-item {
  display: flex;
  justify-content: space-between;
  padding: 12px 0;
  border-bottom: 1px solid #f0f0f0;
}

.meta-label {
  color: #666;
}

.meta-value {
  color: #1a1a2e;
  font-weight: 600;
}

.student-switcher {
  margin-top: 18px;
}

.switcher-title {
  font-size: 13px;
  color: #666;
  margin-bottom: 8px;
}

.archive-list-item {
  padding: 14px 16px;
  border-radius: 12px;
  background: #f8fafc;
  margin-bottom: 12px;
  cursor: pointer;
  border: 1px solid transparent;
}

.archive-list-item.active {
  background: #eef5ff;
  border-color: #b9d7ff;
}

.archive-list-title {
  font-weight: 600;
  color: #1a1a2e;
}

.archive-list-subtitle {
  margin-top: 6px;
  font-size: 12px;
  color: #7b8794;
}

.archive-status-row {
  margin-top: 8px;
}

.review-banner {
  margin-bottom: 18px;
  padding: 14px 16px;
  border-radius: 12px;
  background: #f7fbff;
  border: 1px solid #dbeafe;
}

.review-banner-main {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
}

.review-banner-label {
  font-size: 13px;
  color: #606266;
}

.review-banner-time {
  font-size: 12px;
  color: #909399;
}

.review-comment-box {
  margin-top: 10px;
}

.review-comment-text {
  margin-top: 6px;
  color: #303133;
  line-height: 1.7;
  white-space: pre-wrap;
}

.section-block {
  margin-top: 24px;
  padding-top: 18px;
  border-top: 1px solid #f0f0f0;
}

.section-block-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
}

.section-block-header h3 {
  margin: 0;
  font-size: 16px;
  color: #1a1a2e;
}

.dynamic-card {
  padding: 16px;
  border-radius: 12px;
  background: #fafcff;
  border: 1px solid #edf2f7;
  margin-bottom: 12px;
}

.file-preview-row {
  margin-top: 10px;
}

.file-preview-row a {
  color: #1890ff;
  text-decoration: none;
}

.align-right {
  text-align: right;
}

.danger-text {
  color: #f56c6c;
}

.review-panel {
  background: #fcfcff;
}

.review-actions {
  display: flex;
  gap: 12px;
}

@media (max-width: 768px) {
  .archive-page-title {
    font-size: 24px;
  }

  .archive-header-inner {
    align-items: flex-start;
  }
}
</style>
